import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './_styles_/CurrencyDetailTab.css';
import useExtendedState from '../../hooks/useExtendedState';
import { currencyType } from '../../types';
import Privileges from '../../utils/Privileges';
import { Privileged } from '../../hocs/withPrivileges';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import {
    ALL_CURRENCIES_QUERY_NAME,
    TOGGLE_CURRENCY_ACTIVATION,
    UPDATE_CURRENCY,
} from '../../graphql/currencies';
import useMultiValidator from '../../hooks/useMultiValidator';
import { CURRENCY_NAME_MAX_LENGTH, currencyExchangeRateValidator, currencyNameValidator } from '../../validators/currencies';
import ApplyChangesButton from '../buttons/ApplyChangesButton';
import PrivilegedNumberField from '../fields/privileged/PrivilegedNumberField';
import PrivilegedTextField from '../fields/privileged/PrivilegedTextField';
import ReadonlyField from '../fields/ReadonlyField';
import ExpandedListTab from '../ExpandedListTab';
import Constants from '../../utils/Constants';
import ActivationButton from '../buttons/ActivationButton';
import InfoPanel from '../InfoPanel';
import {
    transformCurrencyDeactivationBlockers,
    transformCurrencyModificationBlockers,
} from '../blockers/CurrencyBlockers';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

CurrencyDetailTab.propTypes = {
    currency: currencyType.isRequired,
};

function CurrencyDetailTab(props) {
    const {
        currency,
    } = props;
    const { t } = useTranslation();
    const nameState = useExtendedState(currency.name);
    const { code } = currency;
    const exchangeRateState = useExtendedState(currency.exchangeRate);
    const { validators, isValid } = useMultiValidator(true, {
        name: compositeValidator(currencyNameValidator(t), wafValidator(t)),
        exchangeRate: compositeValidator(currencyExchangeRateValidator(t), wafValidator(t))
    });
    const isChanged = nameState.isChanged || exchangeRateState.isChanged;
    const canBeModified = currency.modificationBlockers?.length === 0;
    const [isModificationDisabled, setIsModificationDisabled] = useState(true);
    const isChangedOrModifiablePromise = !isChanged || !canBeModified ? Promise.reject() : Promise.resolve();
    Promise.all([isChangedOrModifiablePromise, isValid()])
        .then(() => setIsModificationDisabled(false))
        .catch(() => setIsModificationDisabled(true));

    const statesToResetOnSuccess = [nameState, exchangeRateState];
    const updateCurrencyMutation = useExtendedMutation(UPDATE_CURRENCY, {
        variables: {
            code,
            name: nameState.value,
            exchangeRate: parseFloat(exchangeRateState.value),
        },
        statesToResetOnSuccess,
        successMessageOptions: { key: 'currencies.notification.currency-update-success', dataProperty: 'updateCurrency' },
        failureMessageOptions: { key: 'currencies.notification.currency-update-failure', params: { name: currency.name, code: currency.code } },
        refetchQueries: [ALL_CURRENCIES_QUERY_NAME],
    });
    const isActivationButtonDisabled = currency.active && currency.deactivationBlockers?.length > 0;
    let toggleCurrencyActivationConfirmMessageKey = 'currencies.confirm-activate-currency';
    if (currency.active) {
        toggleCurrencyActivationConfirmMessageKey = 'currencies.confirm-deactivate-currency';
    }
    const toggleCurrencyActivationMutation = useExtendedMutation(TOGGLE_CURRENCY_ACTIVATION, {
        variables: { code: currency.code },
        statesToResetOnSuccess,
        successMessageCreator: ({ toggleCurrencyActivation: { active: updatedActive, name: updatedName, code: updatedCode } }) => {
            let toggleCurrencyActivationSuccessMessageKey = 'currencies.notification.currency-deactivate-success';
            if (updatedActive) {
                toggleCurrencyActivationSuccessMessageKey = 'currencies.notification.currency-activate-success';
            }
            return t(toggleCurrencyActivationSuccessMessageKey, { name: updatedName, code: updatedCode });
        },
        failureMessageCreator: () => {
            let toggleCurrencyActivationFailureMessageKey = 'currencies.notification.currency-activate-failure';
            if (currency.active) {
                toggleCurrencyActivationFailureMessageKey = 'currencies.notification.currency-deactivate-failure';
            }
            return t(toggleCurrencyActivationFailureMessageKey, { name: currency.name, code: currency.code });
        },
        refetchQueries: [ALL_CURRENCIES_QUERY_NAME],
    });
    const sufficientPrivileges = [Privileges.CURRENCY_EDIT];
    return (
        <ExpandedListTab
            className="currency-detail-tab"
            title={t('currencies.currency-detail')}
        >
            <div className="currency-detail-tab--readonly-fields">
                <ReadonlyField
                    label={t('currencies.active')}
                    value={currency.active ? t('misc.yes') : t('misc.no')}
                    className="currency-detail-tab--fields--active"
                />
            </div>
            <div className="rich-list-row-expanded--fields">
                <ReadonlyField
                    label={t('currencies.code')}
                    value={code}
                    className="currency-detail-tab--fields--code"
                />
                <PrivilegedTextField
                    sufficientPrivileges={canBeModified ? sufficientPrivileges : []}
                    label={t('currencies.name')}
                    state={nameState}
                    validator={validators.name}
                    className="currency-detail-tab--fields--name wide"
                    placeholder={t('currencies.placeholder.name')}
                    maxLength={CURRENCY_NAME_MAX_LENGTH}
                    isRequired
                />
                <PrivilegedNumberField
                    sufficientPrivileges={canBeModified ? sufficientPrivileges : []}
                    label={t('currencies.exchange-rate')}
                    state={exchangeRateState}
                    fractionDigits={4}
                    step={Constants.DEFAULT_EXVHANGE_RATE_STEP}
                    onChange={(newValue) => exchangeRateState.setValue(parseFloat(newValue))}
                    postfix={t('currencies.exchange-rate-to-euro')}
                    validator={validators.exchangeRate}
                    className="currency-detail-tab--fields--exchange-rate"
                    isRequired
                />
            </div>
            <Privileged sufficientPrivileges={sufficientPrivileges}>
                <div className="rich-list-row-expanded--buttons">
                    <div className="rich-list-row-expanded--buttons--left">
                        <ApplyChangesButton
                            extendedMutation={updateCurrencyMutation}
                            disabled={isModificationDisabled}
                            confirmationMessage={t('currencies.confirm-update-currency', { name: currency.name, code: currency.code })}
                        />
                    </div>
                    <div className="rich-list-row-expanded--buttons--middle">
                        <ActivationButton
                            isActive={currency.active}
                            disabled={isActivationButtonDisabled}
                            extendedMutation={toggleCurrencyActivationMutation}
                            confirmationMessage={t(toggleCurrencyActivationConfirmMessageKey, { name: currency.name, code: currency.code })}
                        />
                    </div>
                    <div className="rich-list-row-expanded--buttons--right" />
                </div>
                {currency.modificationBlockers?.map((modificationBlocker) => (
                    <InfoPanel
                        key={modificationBlocker}
                        message={transformCurrencyModificationBlockers(t, modificationBlocker)}
                    />
                ))}
                {currency.deactivationBlockers?.map((deactivationBlocker) => (
                    <InfoPanel
                        key={deactivationBlocker}
                        message={transformCurrencyDeactivationBlockers(t, deactivationBlocker)}
                    />
                ))}
            </Privileged>
        </ExpandedListTab>
    );
}

export default CurrencyDetailTab;
